import { useQueryClient } from '@tanstack/react-query'
import { Replace, SendHorizontal, Upload, Wand } from 'lucide-react'
import pluralize from 'pluralize'
import { useState } from 'react'
import { toast } from 'sonner'

import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CopyToClipboard } from '@/components/ui/CopyToClipboard'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldDescription } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import { sendCOIToAllHolders, useCurrentCOIDocuments, useSendableHolderCount } from '../../../../models/coi'
import { useCurrentUser } from '../../../../models/user'
import { AddCertificateHolderDialog } from './AddCertificateHolderDialog'
import { BulkEditCertificateDialog } from './BulkEditCertificateDialog'
import { ImportCertificateHoldersDialog } from './ImportCertificateHoldersDialog'
import { useCertificateHolderImport } from './useCertificateHolderImport'

interface COIHeaderProps {
  email?: string
  orgId?: string
  manualMode?: boolean
}

export function COIHeader({ email, orgId, manualMode }: COIHeaderProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sendAllOpen, setSendAllOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  // Its own instance rather than one shared with the holders tab: the tab's
  // empty-state dropzone reports its own progress, and both invalidate the same
  // holder query, so the list refreshes whichever control was used.
  const { importFile, isUploading, isImporting } = useCertificateHolderImport(orgId)
  const [sendingAll, setSendingAll] = useState(false)
  const [ccTeam, setCcTeam] = useState(false)
  const queryClient = useQueryClient()
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = !!user?.isCaiAdmin
  // Drives both whether the CTA is worth showing and the count it confirms with.
  const { sendableCount } = useSendableHolderCount(orgId)
  // Only worth offering once there is somewhere else to move holders to.
  const { documents } = useCurrentCOIDocuments(orgId)
  const canBulkEdit = isCaiAdmin && documents.filter((d) => !!d.selectionKey).length > 1

  function handleSuccess() {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
  }

  async function handleSendAll() {
    setSendingAll(true)
    try {
      const dispatched = await sendCOIToAllHolders(orgId, ccTeam)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_SENT_LOGS] })
      setSendAllOpen(false)
      toast.success(
        `Sending your COI to ${pluralize('certificate holder', dispatched, true)}. The sent log updates as each one goes out.`
      )
    } catch (error) {
      toast.error(parseError(error))
    } finally {
      setSendingAll(false)
    }
  }

  return (
    <div className="@container">
      <div className="grid grid-cols-1 gap-10 @5xl:grid-cols-2">
        <div className="flex flex-col gap-6">
          <h1 className="text-2xl font-bold">Certificates (COI)</h1>
          <OnboardingBannerSlot />
          <p className="text-muted-foreground">
            {manualMode
              ? 'Manage and send proof of insurance to your partners.'
              : "Manage and send proof of insurance to your partners. We'll instantly generate and send verified COIs to any broker or shipper as a certificate holder."}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => setDialogOpen(true)}>
              {manualMode
                ? isCaiAdmin
                  ? 'Add certificate holder'
                  : 'Request certificate holder'
                : 'Add holder & send COI'}
            </Button>
            {sendableCount > 0 && (
              <Button variant="outline" onClick={() => setSendAllOpen(true)}>
                <SendHorizontal className="size-4" />
                Send to all holders
              </Button>
            )}
            {canBulkEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Change which certificate holders receive"
                    onClick={() => setBulkEditOpen(true)}
                  >
                    <Replace className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Change which certificate holders receive</TooltipContent>
              </Tooltip>
            )}
            {/* Icon-only so all three actions sit on one row. */}
            {!manualMode && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Import certificate holders"
                    onClick={() => setImportDialogOpen(true)}
                    disabled={isUploading}
                  >
                    <Upload className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Import certificate holders</TooltipContent>
              </Tooltip>
            )}
            {isImporting && (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <Spinner className="size-4" />
                Importing certificate holders — they appear in the list as they are added.
              </div>
            )}
          </div>
        </div>

        {!manualMode && email && (
          <Card className="border-none shadow-none" style={{ backgroundColor: '#BFDEFF80' }}>
            <CardContent className="flex gap-4">
              <div className="bg-surface flex size-10 shrink-0 items-center justify-center rounded-full">
                <Wand className="text-cai-blue size-5" />
              </div>
              <div className="flex flex-col gap-4">
                <p className="font-semibold">Quick COI generation</p>
                <p className="text-muted-foreground text-sm">
                  Forward any request from a broker or shipper to{' '}
                  <span className="text-foreground font-medium">{email}</span>. We&apos;ll automatically
                  create the holder and send your proof of insurance in seconds.
                </p>
                <CopyToClipboard text={email} label="Copy email" copiedLabel="Copied" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <AddCertificateHolderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={handleSuccess}
        orgId={orgId}
        manualMode={manualMode}
      />

      <BulkEditCertificateDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        documents={documents}
        orgId={orgId}
      />

      <ImportCertificateHoldersDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={importFile}
        isUploading={isUploading}
      />

      <Dialog
        open={sendAllOpen}
        onOpenChange={(open) => {
          setSendAllOpen(open)
          if (open) setCcTeam(false)
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Send COI to all certificate holders</DialogTitle>
            <DialogDescription>
              Your current certificate of insurance will be emailed to{' '}
              <span className="text-foreground font-medium">
                {pluralize('certificate holder', sendableCount, true)}
              </span>
              , each at every email address on file. Holders without a COI document or a valid email address
              are skipped.
            </DialogDescription>
          </DialogHeader>
          <Field orientation="horizontal">
            <div className="flex-1">
              <Label className="text-md mb-1">Copy the account owners</Label>
              <FieldDescription className="text-sm">
                Adds your business owners and Cai rep to the CC line of every message — one copy each per
                certificate holder.
              </FieldDescription>
            </div>
            <Switch checked={ccTeam} onCheckedChange={setCcTeam} disabled={sendingAll} />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendAllOpen(false)} disabled={sendingAll}>
              Cancel
            </Button>
            <Button onClick={handleSendAll} loading={sendingAll ? 'Sending…' : undefined}>
              Send to all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
